import {
  Avatar,
  Breadcrumb,
  Button,
  Card,
  Col,
  Divider,
  Row,
  Tag,
  Typography,
  Grid,
  Skeleton,
} from "antd";
import { TableProps } from "antd/lib/table";
import { format } from "date-fns";
import { Table } from "components/base";
import { breadcrumbIcon, settingsIcon2 } from "svgs";
import { KeyValue } from "types";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "store";
import React, { useCallback, useMemo, useEffect } from "react";
import { DATE_FORMAT, FULL_DATE_FORMAT } from "../../../../utils/constants";
import { getDateFromSeconds } from "../../../../utils/helpers";
import { IssueStatus, IssueType } from "protobuf/issue_service_pb";
import { issueTypeCell, statusCell } from "../cell-renders";
import { useHistory, useParams } from "react-router-dom";
import { getIssue } from "../../../../store/actions";
import { ArrowLeftOutlined } from "@ant-design/icons";

import "styles/components/tab-content/issues-content/issue-details.scss";

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

type IssueDetailParams = {
  issueId?: string;
};

export default function Detail() {
  const dispatch = useDispatch();
  const history = useHistory();
  const screens = useBreakpoint();
  const { issueId } = useParams<IssueDetailParams>();

  // Determine screen size
  const isMobile = !screens.md;
  const isTablet = screens.md && !screens.lg;
  const isSmallMobile = !screens.sm;

  const selectedIssue = useSelector((state: AppState) => state.issues.selectedIssue);
  const loading = useSelector((state: AppState) => (state.issues as any).loading);

  const onClickBack = useCallback(() => {
    history.goBack();
  }, [history]);

  // Fetch issue if not available
  useEffect(() => {
    if (!selectedIssue && issueId) {
      dispatch(getIssue({ id: issueId }));
    }
  }, [dispatch, issueId, selectedIssue]);

  const initialValues = useMemo(() => {
    return {
      id: selectedIssue?.id || "",
      serialId: selectedIssue?.serialId || 0,
      reportedBy: selectedIssue?.reportedBy || "",
      vehicleId: selectedIssue?.vehicleId || "",
      addedAt: selectedIssue?.addedAt || 0,
      status: selectedIssue?.status || IssueStatus.Enum,
      issue_type: selectedIssue?.type || IssueType.Enum,
      assignedTo: selectedIssue?.assignedTo || "",
      ownerOrgId: selectedIssue?.ownerOrgId || "",
      dueDate: selectedIssue?.dueDate || 0,
      riderId: selectedIssue?.riderId || "",
      riderName: selectedIssue?.riderName || "",
      createdByName: selectedIssue?.createdByName || "",
      assigneeName: selectedIssue?.assigneeName || "",
      vehiclePate: selectedIssue?.vehiclePate || "",
      issue_other_type: selectedIssue?.issueOtherTypeText || "",
    };
  }, [selectedIssue]);

  // Loading state
  if (loading) {
    return (
      <section className="detail-container">
        <div className="detail-breadcrumb">
          <Skeleton.Button active size="small" style={{ width: 150 }} />
        </div>
        <Card className="detail-card">
          <Skeleton active paragraph={{ rows: 8 }} />
        </Card>
      </section>
    );
  }

  // Get column span based on screen size
  const getColSpan = () => {
    if (isSmallMobile) return 24; // Full width
    if (isMobile) return 24; // Full width
    if (isTablet) return 12; // Half width
    return 12; // Half width on desktop
  };

  return (
    <section className="detail-container">
      {/* Breadcrumb Navigation */}
      <div className="detail-breadcrumb">
        {isMobile ? (
          // Mobile: Back button with arrow
          <Button
            type="link"
            onClick={onClickBack}
            className="back-button"
            icon={<ArrowLeftOutlined />}
          >
            Back to Issues
          </Button>
        ) : (
          // Desktop/Tablet: Full breadcrumb
         <div className="battery-detail-header">
                <Breadcrumb className="battery-breadcrumb" separator={breadcrumbIcon}>
                    <Breadcrumb.Item>
                        <Button type="link" className="batteries-link" onClick={onClickBack}>
                            Issues
                        </Button>
                    </Breadcrumb.Item>
                    <Breadcrumb.Item>Details</Breadcrumb.Item>
                </Breadcrumb>
            </div>
        )}
      </div>

      {/* Main Issue Detail Card */}
      <Card className="detail-card">
        {/* Card Header */}
        <div className="card-header">
          <div className="header-info">
            <Title level={isMobile ? 5 : 4} className="issue-title">
              {selectedIssue?.issueOtherTypeText || "Issue Details"}
            </Title>
            <Text type="secondary" className="issue-id">
              Issue ID: {selectedIssue?.serialId || "—"}
            </Text>
          </div>
          <div className="header-status">
            {statusCell(selectedIssue?.status || IssueStatus.Enum.PENDING)}
          </div>
        </div>

        <Divider className="header-divider" />

        {/* Details Grid */}
        <Row gutter={[isMobile ? 16 : 32, isMobile ? 16 : 24]}>
          {/* Issue Type */}
          <Col xs={24} sm={24} md={12} lg={12}>
            <div className="detail-item">
              <Text type="secondary" className="detail-label">
                Issue Type
              </Text>
              <div className="detail-value">
                {issueTypeCell(selectedIssue?.type || IssueType.Enum.UNKNOWN)}
              </div>
            </div>
          </Col>

          {/* Vehicle ID */}
          <Col xs={24} sm={24} md={12} lg={12}>
            <div className="detail-item">
              <Text type="secondary" className="detail-label">
                Vehicle ID
              </Text>
              <div className="detail-value">
                {selectedIssue?.vehiclePate || "—"}
              </div>
            </div>
          </Col>

          {/* Created On */}
          <Col xs={24} sm={12} md={12} lg={12}>
            <div className="detail-item">
              <Text type="secondary" className="detail-label">
                Created On
              </Text>
              <div className="detail-value">
                {selectedIssue?.addedAt
                  ? format(
                      getDateFromSeconds(selectedIssue.addedAt),
                      isMobile ? DATE_FORMAT : FULL_DATE_FORMAT
                    )
                  : "—"}
              </div>
            </div>
          </Col>

          {/* Due Date */}
          <Col xs={24} sm={12} md={12} lg={12}>
            <div className="detail-item">
              <Text type="secondary" className="detail-label">
                Due Date
              </Text>
              <div className="detail-value">
                {selectedIssue?.dueDate
                  ? format(getDateFromSeconds(selectedIssue.dueDate), DATE_FORMAT)
                  : "Not set yet"}
              </div>
            </div>
          </Col>

          {/* Rider Name */}
          <Col xs={24} sm={12} md={12} lg={12}>
            <div className="detail-item">
              <Text type="secondary" className="detail-label">
                Rider Name
              </Text>
              <div className="detail-value with-avatar">
                {selectedIssue?.riderName ? (
                  <>
                    <Avatar size={isMobile ? 28 : 24} className="user-avatar">
                      {selectedIssue.riderName[0]}
                    </Avatar>
                    <span className="user-name">{selectedIssue.riderName}</span>
                  </>
                ) : (
                  "—"
                )}
              </div>
            </div>
          </Col>

          {/* Assignee */}
          <Col xs={24} sm={12} md={12} lg={12}>
            <div className="detail-item">
              <Text type="secondary" className="detail-label">
                Assignee
              </Text>
              <div className="detail-value with-avatar">
                {selectedIssue?.assigneeName ? (
                  <>
                    <Avatar size={isMobile ? 28 : 24} className="user-avatar">
                      {selectedIssue.assigneeName[0]}
                    </Avatar>
                    <span className="user-name">
                      {selectedIssue.assigneeName}
                    </span>
                  </>
                ) : (
                  <Tag className="not-assigned-tag">Not Assigned</Tag>
                )}
              </div>
            </div>
          </Col>

          {/* Created By */}
          <Col xs={24} sm={12} md={12} lg={12}>
            <div className="detail-item">
              <Text type="secondary" className="detail-label">
                Created By
              </Text>
              <div className="detail-value with-avatar">
                {selectedIssue?.createdByName ? (
                  <>
                    <Avatar size={isMobile ? 28 : 24} className="user-avatar">
                      {selectedIssue.createdByName[0]}
                    </Avatar>
                    <span className="user-name">
                      {selectedIssue.createdByName}
                    </span>
                  </>
                ) : (
                  "—"
                )}
              </div>
            </div>
          </Col>

          {/* Issue Description - Full width */}
          {selectedIssue?.issueOtherTypeText && (
            <Col xs={24} sm={24} md={24} lg={24}>
              <div className="detail-item description">
                <Text type="secondary" className="detail-label">
                  Issue Description
                </Text>
                <div className="detail-value description-text">
                  {selectedIssue.issueOtherTypeText}
                </div>
              </div>
            </Col>
          )}
        </Row>
      </Card>
    </section>
  );
}

function renderUtilizationStatus(value: number) {
  const status = value;
  switch (status) {
    case 0:
      return "In the Lot";
    case 1:
      return "Checked Out";
    case 2:
      return "In the Lot";
    case 3:
      return "In the Lot";
    case 4:
      return "In the Lot";
    case 5:
      return "In the Lot";
    default:
      return "Checked Out";
  }
}

function functionalStatusCell(value: number) {
  const status = value;
  switch (status) {
    case 0:
      return "Unknown";
    case 1:
      return "Ride In-progress";
    case 2:
      return "In Maintenance";
    case 3:
      return "Marked Broken";
    case 4:
      return "Idle";
    case 5:
      return "Retired";
    default:
      return "";
  }
}